/* assets/mw-card-icons.js
  - Loads SVG icons into: .mw-card-icon[data-mw-icon]
  - Handles actions from BOTH systems:
      A) New system (recommended):
         .mw-card-action[data-mw-action="add-to-cart|quick-view|compare"]
         + data-variant-id / data-handle
      B) Old system (backward compatible):
         [data-mw-add-to-cart] inside .mw-card-icons wrapper
         wrapper provides data-variant-id / data-product-handle / data-product-url
  - Fixes "cart empty until close" by:
      1) Add to cart
      2) Wait for cart backend update
      3) Fetch sections properly
      4) Update icon + drawer + cart page (best effort)
      5) Dispatch update events
      6) Open drawer
*/

const ICON_MAP = {
  'add-to-cart': 'mw-icon-camera.svg',
  'quick-view': 'mw-icon-warranty.svg',
  'compare': 'mw-icon-shipping.svg'
};

(function () {
  if (window.__MW_CARD_ICONS_BOUND__) return;
  window.__MW_CARD_ICONS_BOUND__ = true;

  // ---------- helpers ----------
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

  function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }
  function toNum(v) { const n = Number(v); return Number.isFinite(n) ? n : null; }

  async function fetchCartJSON() {
    try {
      const res = await fetch(`${ROOT}cart.js`, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  // Wait until cart reflects the added variant (fixes "empty until close")
  async function waitForCartUpdate(variantId, prevCount) {
    const vid = String(variantId);
    for (let i = 0; i < 15; i++) { // ~1.8s
      const cart = await fetchCartJSON();
      if (cart) {
        const nowCount = Number(cart.item_count || 0);
        const hasVariant = (cart.items || []).some(it => String(it.variant_id) === vid);
        if (hasVariant || nowCount > prevCount) return cart;
      }
      await sleep(120);
    }
    return null;
  }

  // ---------- ICON INJECTION ----------
  async function injectIcons(root = document) {
    const nodes = root.querySelectorAll('.mw-card-icon[data-mw-icon]:not([data-mw-loaded])');
    if (!nodes.length) return;

    const cache = window.__MW_ICON_CACHE__ || (window.__MW_ICON_CACHE__ = new Map());

    await Promise.all([...nodes].map(async (el) => {
      const key = (el.getAttribute('data-mw-icon') || '').trim();
      const file = ICON_MAP[key];
      if (!file) return;

      el.setAttribute('data-mw-loaded', '1');

      if (!cache.has(file)) {
        try {
          const res = await fetch(`${ROOT}assets/${file}`, { cache: 'force-cache' });
          cache.set(file, res.ok ? await res.text() : '');
        } catch {
          cache.set(file, '');
        }
      }

      const svg = cache.get(file);
      if (svg) el.innerHTML = svg;
    }));
  }

  // initial + section re-renders
  injectIcons(document);
  document.addEventListener('shopify:section:load', (e) => injectIcons(e.target));
  document.addEventListener('shopify:section:reorder', () => injectIcons(document));
  document.addEventListener('shopify:section:select', (e) => injectIcons(e.target));

  // ---------- SECTIONS FETCH (Shopify correct) ----------
  function collectCartSectionIds() {
    const ids = new Set();

    // common Horizon ids
    ids.add('cart-drawer');
    ids.add('cart-icon-bubble');

    // cart-items-component sections
    document.querySelectorAll('cart-items-component').forEach(comp => {
      const sid = comp.dataset?.sectionId;
      if (sid) ids.add(sid);
    });

    // main cart container (best effort)
    const mainCart = document.querySelector('[id*="main-cart"]');
    if (mainCart && mainCart.id) ids.add(mainCart.id);

    // any section ids containing cart/drawer
    document.querySelectorAll('[data-section-id]').forEach(el => {
      const sid = el.dataset.sectionId;
      if (sid && (sid.includes('cart') || sid.includes('drawer'))) ids.add(sid);
    });

    return Array.from(ids);
  }

  async function fetchSections(sectionIds) {
    if (!sectionIds || !sectionIds.length) return null;
    const params = sectionIds.map(id => `sections[]=${encodeURIComponent(id)}`).join('&');
    const url = `${ROOT}?${params}&t=${Date.now()}`;

    try {
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      return await res.json(); // { id: "<html>" }
    } catch {
      return null;
    }
  }

  // ---------- DOM updates ----------
  function updateCartIcon(html) {
    if (!html) return false;
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('#cart-icon-bubble');
      const current = document.querySelector('#cart-icon-bubble');
      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;
        return true;
      }

      // fallback cart counts
      const freshCount = doc.querySelector('[data-cart-count]');
      const currentCounts = document.querySelectorAll('[data-cart-count]');
      if (freshCount && currentCounts.length) {
        const count = freshCount.textContent || '0';
        currentCounts.forEach(el => {
          el.textContent = count;
          el.classList.toggle('hidden', count.trim() === '0');
        });
        return true;
      }
    } catch {}
    return false;
  }

  function updateDrawerContent(sections) {
    const drawerHTML = sections && sections['cart-drawer'];
    if (!drawerHTML) return false;

    try {
      const doc = new DOMParser().parseFromString(drawerHTML, 'text/html');
      const currentDrawer = document.querySelector('cart-drawer-component');
      if (!currentDrawer) return false;

      // Prefer morph if available
      if (window.morph && typeof window.morph === 'function') {
        const freshDrawer = doc.querySelector('cart-drawer-component');
        if (freshDrawer) {
          window.morph(currentDrawer, freshDrawer);
          return true;
        }
      }

      // Best-effort partial updates
      const pairs = [
        ['.cart-drawer__content', '.cart-drawer__content'],
        ['.cart-drawer__summary', '.cart-drawer__summary'],
        ['.cart-items', '.cart-items'],
        ['[data-cart-drawer-inner]', '[data-cart-drawer-inner]']
      ];

      let updated = false;
      for (const [selFresh, selCur] of pairs) {
        const fresh = doc.querySelector(selFresh);
        const cur = currentDrawer.querySelector(selCur);
        if (fresh && cur) {
          cur.innerHTML = fresh.innerHTML;
          updated = true;
        }
      }
      if (updated) return true;

      // last resort: dialog content
      const dialog = currentDrawer.querySelector('dialog');
      const freshDialog = doc.querySelector('cart-drawer-component dialog');
      if (dialog && freshDialog) {
        dialog.innerHTML = freshDialog.innerHTML;
        return true;
      }
    } catch {}
    return false;
  }

  function updateCartPage(sections) {
    const mainCart = document.querySelector('[id*="main-cart"]');
    if (!mainCart || !mainCart.id) return false;

    const html = sections && sections[mainCart.id];
    if (!html) return false;

    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`#${CSS.escape(mainCart.id)}`);
      if (fresh) {
        mainCart.innerHTML = fresh.innerHTML;
        return true;
      }
    } catch {}
    return false;
  }

  function triggerCartUpdateEvents(cartData, sections) {
    // Horizon style
    document.dispatchEvent(new CustomEvent('theme:cart:update', {
      detail: {
        data: cartData,
        sections: sections,
        itemCount: cartData?.item_count || 0,
        source: 'mw'
      },
      bubbles: true,
      composed: true
    }));

    // alternate
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: cartData,
      bubbles: true
    }));

    // optional global
    if (typeof window.cart !== 'undefined') window.cart = cartData;
  }

  async function openCartDrawer() {
    await sleep(80);

    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }

    if (drawer) {
      const dialog = drawer.querySelector('dialog');
      if (dialog && typeof dialog.showModal === 'function') {
        try {
          dialog.showModal();
          drawer.classList.add('is-open');
          return true;
        } catch {}
      }
    }

    // theme listeners
    document.dispatchEvent(new CustomEvent('theme:cart:add', {
      detail: { shouldOpenDrawer: true },
      bubbles: true,
      composed: true
    }));

    await sleep(50);

    // fallback click
    const cartIcon = document.querySelector('[data-cart-icon], .cart-icon, #cart-icon-bubble');
    if (cartIcon && cartIcon instanceof HTMLElement) {
      cartIcon.click();
      return true;
    }

    return false;
  }

  // ---------- core add to cart pipeline ----------
  async function addToCartAndRefreshUI(variantId) {
    const prevCart = await fetchCartJSON();
    const prevCount = Number(prevCart?.item_count || 0);

    // add
    const fd = new FormData();
    fd.append('id', String(variantId));
    fd.append('quantity', '1');

    const addRes = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: fd
    });

    if (!addRes.ok) {
      const errorData = await addRes.json().catch(() => ({}));
      throw new Error(errorData?.description || 'Failed to add to cart');
    }

    // wait backend
    const updatedCart = await waitForCartUpdate(variantId, prevCount);
    const finalCart = updatedCart || await fetchCartJSON();

    // fetch sections and update UI (best effort)
    const sectionIds = collectCartSectionIds();
    const sections = await fetchSections(sectionIds);

    if (sections) {
      // update icon + drawer + cart page
      if (sections['cart-icon-bubble']) updateCartIcon(sections['cart-icon-bubble']);
      updateDrawerContent(sections);
      updateCartPage(sections);
      triggerCartUpdateEvents(finalCart, sections);

      // icons may exist inside updated HTML
      injectIcons(document);
    } else {
      // even if sections fail, still dispatch update so theme can react
      triggerCartUpdateEvents(finalCart, null);
    }

    await sleep(120);
    await openCartDrawer();

    return finalCart;
  }

  // ---------- CLICK HANDLER (supports both systems) ----------
  document.addEventListener('click', async (e) => {
    // NEW SYSTEM: .mw-card-action
    const actionBtn = e.target.closest('.mw-card-action[data-mw-action]');
    if (actionBtn) {
      const action = actionBtn.getAttribute('data-mw-action');

      // prevent card link click
      e.preventDefault();
      e.stopPropagation();

      if (action === 'quick-view') {
        const handle = actionBtn.getAttribute('data-handle');
        document.dispatchEvent(new CustomEvent('mw:quickview', {
          detail: { handle, source: actionBtn }
        }));
        return;
      }

      if (action === 'compare') {
        const handle = actionBtn.getAttribute('data-handle') || actionBtn.getAttribute('data-product-handle');
        document.dispatchEvent(new CustomEvent('mw:compare:add', {
          detail: { handle, source: actionBtn }
        }));
        return;
      }

      if (action === 'add-to-cart') {
        const variantId = toNum(actionBtn.getAttribute('data-variant-id'));
        const handle = actionBtn.getAttribute('data-handle') || actionBtn.getAttribute('data-product-handle');

        if (!variantId) {
          // fallback to product page
          if (handle) window.location.href = `${ROOT}products/${handle}`;
          return;
        }

        const wasDisabled = actionBtn.disabled;
        actionBtn.disabled = true;
        actionBtn.classList.add('loading');

        try {
          await addToCartAndRefreshUI(variantId);
        } catch (err) {
          alert(err?.message || 'Failed to add to cart. Please try again.');
          if (handle) window.location.href = `${ROOT}products/${handle}`;
        } finally {
          actionBtn.disabled = wasDisabled;
          actionBtn.classList.remove('loading');
        }
        return;
      }
    }

    // OLD SYSTEM (backward compatible)
    const qvBtnOld = e.target.closest('[data-mw-quickview]');
    if (qvBtnOld) {
      const wrap = qvBtnOld.closest('.mw-card-icons');
      const url = wrap?.getAttribute('data-product-url');
      if (url) window.location.href = url;
      return;
    }

    const cartBtnOld = e.target.closest('[data-mw-add-to-cart]');
    if (!cartBtnOld) return;

    e.preventDefault();
    e.stopPropagation();

    const wrap = cartBtnOld.closest('.mw-card-icons');
    const variantIdOld = toNum(wrap?.getAttribute('data-variant-id'));
    const handleOld = wrap?.getAttribute('data-product-handle');

    if (!variantIdOld) {
      if (handleOld) window.location.href = `${ROOT}products/${handleOld}`;
      return;
    }

    const wasDisabled = cartBtnOld.disabled;
    cartBtnOld.disabled = true;
    cartBtnOld.classList.add('loading');

    try {
      await addToCartAndRefreshUI(variantIdOld);
    } catch (err) {
      alert(err?.message || 'Failed to add to cart. Please try again.');
      if (handleOld) window.location.href = `${ROOT}products/${handleOld}`;
    } finally {
      cartBtnOld.disabled = wasDisabled;
      cartBtnOld.classList.remove('loading');
    }
  });

  // keep icons injected if DOM changes (simple observer)
  const obs = new MutationObserver(() => injectIcons(document));
  obs.observe(document.documentElement, { childList: true, subtree: true });

  console.log('✅ MW Card Icons initialized (icons + actions)');
})();
